import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { useBrandingStore } from '@shared/stores/brandingStore';
import { useModuleStore } from '@shared/stores/moduleStore';

/** Per-image preload timeout and the absolute cap on how long we keep the splash up. */
export const IMAGE_PRELOAD_TIMEOUT_MS = 2500;
export const SPLASH_MAX_WAIT_MS = 5000;

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Show the native splash (covers a re-brand, e.g. sign-out). No-op on web. */
export async function showNativeSplash(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await SplashScreen.show({ autoHide: false });
  } catch {
    /* noop */
  }
}

/** Hide the native splash. No-op on web. */
export async function hideNativeSplash(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await SplashScreen.hide();
  } catch {
    /* noop */
  }
}

/** Remove the static splash element rendered by index.html (web). */
export function hideWebSplashGate(): void {
  if (typeof document === 'undefined') return;
  const splash = document.getElementById('splash-gate');
  if (splash) {
    splash.style.transition = 'opacity 280ms cubic-bezier(0.32, 0.72, 0, 1)';
    splash.style.opacity = '0';
    setTimeout(() => splash.remove(), 320);
  }
}

/** Fetch an image into the WebView/browser cache; resolves on load, error, or timeout. */
function preloadImage(url: string, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    if (!url || typeof Image === 'undefined') {
      resolve();
      return;
    }
    const img = new Image();
    const finish = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(finish, timeoutMs);
    img.onload = finish;
    img.onerror = finish;
    img.src = url;
  });
}

/**
 * Warm the device cache with the currently-resolved branding images (logo,
 * favicon, auth/welcome backgrounds) so the next painted screen shows them
 * instantly with no flash. Bounded per-image.
 */
export async function preloadBrandImages(timeoutMs = IMAGE_PRELOAD_TIMEOUT_MS): Promise<void> {
  const branding = useBrandingStore.getState().branding;
  let imagery: { loginBackground?: string; welcomeHero?: string; signupBackground?: string; forgotBackground?: string } = {};
  try {
    imagery = useModuleStore.getState().getDomainImagery?.() ?? {};
  } catch {
    /* getDomainImagery unavailable */
  }
  const urls = [
    branding?.logoUrl,
    branding?.logoSmallUrl,
    branding?.faviconUrl,
    imagery.loginBackground,
    imagery.welcomeHero,
    imagery.signupBackground,
    imagery.forgotBackground,
  ].filter((u): u is string => !!u);
  const unique = Array.from(new Set(urls));
  await Promise.all(unique.map((u) => preloadImage(u, timeoutMs)));
}
